import * as Yup from 'yup';
import PropTypes from 'prop-types';
import React from 'react';
import { useBoolean } from 'usehooks-ts';
import { useFormik } from 'formik';
import { useLocation, Link, useHistory } from 'react-router-dom';

import AppInputNumberValidation from 'components/App/InputNumberValidation';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Card from 'components/App/Card';
import FormCreatePromotion from 'components/FormCreatePromotion';
import ModalPromotionSummarize from 'components/Modals/ModalPromotionSummarize';

import DATES_OF_WEEK from 'constants/datesOfWeek';
import INITIAL_VALUE from 'constants/initialValue';
import { LOCATIONS, LOGIN_TYPE, storageKey, themeColor } from 'constants/index';
import { PROMOTION_STATUS, PROMOTION_TYPE } from 'constants/promotion';
import cn from 'utils/classnames';
import { parseCurrency } from 'utils';
import { createPromotionValidation } from 'utils/validate';

import { ReactComponent as EarlyBookingPromotionIcon } from 'assets/images/early-booking-promotion.svg';

import styles from './CreateEarlyPromotion.module.scss';

// BREADCRUMB constant moved inside component dynamically

function CreateEarlyPromotion(props) {
  const {
    isLoadingCreatePromotion,

    roomOfPlaceList,
    placePriceTypes,
    promotionDetail,

    actionGetPromotionDetail,
    actionGetListRoomsOfPlace,
    actionGetPlacePriceTypes,
    actionPostPromotion,
    actionEditPromotion,
    actionClearDataPromotionDetail,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const promotionId = new URLSearchParams(location.search).get('promotion');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const breadcrumbList = React.useMemo(() => [
    {
      name: 'Chương trình khuyến mãi',
      isActive: false,
      link: '',
    },
    {
      name: 'Tạo chương trình khuyến mãi',
      isActive: false,
      link: placeId
        ? `${LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION}?place=${placeId}`
        : LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION,
    },
    {
      name: 'Ưu Đãi Đặt Sớm',
      isActive: true,
      link: '',
    },
  ], [placeId]);

  const {
    value: isOpenModal,
    setTrue: onOpenModal,
    setFalse: onCloseModal,
  } = useBoolean(false);

  const validation = useFormik({
    initialValues: {
      rooms: [],
      isAllRooms: true,
      isAllPriceTypes: true,
      applyBookingDays: DATES_OF_WEEK,
      priceTypes: [],
      bookingStartAt: '',
      bookingEndAt: '',
      stayAt: [{
        _id: '1',
        startAt: '',
        endAt: '',
      }],
      discountRate: '',
      discountType: PROMOTION_TYPE.BOOK_EARLY,
      name: '',
      minEarlyDays: 7,
    },

    validationSchema: Yup.object({
      rooms: createPromotionValidation.rooms,
      priceTypes: createPromotionValidation.priceTypes,
      applyBookingDays: createPromotionValidation.applyBookingDays,
      discountRate: createPromotionValidation.discountRate,
      bookingStartAt: createPromotionValidation.bookingStartAt,
      bookingEndAt: createPromotionValidation.bookingEndAt,
      stayAt: createPromotionValidation.stayAt,
      name: createPromotionValidation.name,
      minEarlyDays: createPromotionValidation.minEarlyDays,
    }),

    onSubmit: () => {
      onOpenModal();
    },
  });

  const onCallback = React.useCallback(() => {
    onCloseModal();
    history.push({
      pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
      search: `?place=${placeId}&status=PENDING`,
    });
  }, [placeId, promotionId]);

  const onSubmitCreatePromotion = React.useCallback(() => {
    const newPromotion = {
      ...validation.values,
      minEarlyDays: parseCurrency(validation.values.minEarlyDays, true),
      applyBookingDays: validation.values.applyBookingDays
        .filter((item) => item.isChecked)
        .map((item) => item.value),
      stayAt: validation.values.stayAt.map((item) => ({
        startAt: item.startAt,
        endAt: item.endAt,
      })),
    };

    if (promotionId) {
      actionEditPromotion({
        placeId,
        promotionId,
        promotion: newPromotion,
        callback: onCallback,
      });
    } else {
      actionPostPromotion({
        placeId,
        promotion: newPromotion,
        callback: onCallback,
      });
    }
  }, [validation.values, placeId, promotionId]);

  React.useEffect(() => {
    if (placeId) {
      actionGetListRoomsOfPlace({ placeId });
      actionGetPlacePriceTypes({ placeId });
    }
  }, [placeId]);

  React.useEffect(() => {
    if (promotionId && placeId) {
      actionGetPromotionDetail({ placeId, promotionId });
    }

    return () => {
      actionClearDataPromotionDetail();
    };
  }, [promotionId, placeId]);

  React.useEffect(() => {
    if (Object.keys(promotionDetail).length > 0) {
      validation.setValues((values) => ({
        ...values,
        ...promotionDetail,
        discountRate: promotionDetail.discount,
        applyBookingDays: promotionDetail.applyBookingDays ? DATES_OF_WEEK.map((item) => ({
          ...item,
          isChecked: promotionDetail.applyBookingDays.includes(item.value),
        })) : DATES_OF_WEEK,
      }));
    }

    return () => {
      validation.resetForm();
    };
  }, [promotionDetail]);

  return (
    <div className={cn('page-content', styles.Page)}>
      <Breadcrumb
        pageTitle="Tạo Ưu Đãi Đặt Sớm"
        breadcrumbList={breadcrumbList}
      />

      <div className={styles.Wrapper}>
        <Card className={styles.Section}>
          <div className={styles.SectionTop}>
            <EarlyBookingPromotionIcon className={styles.Icon} />

            <div className={styles.SectionTopTitle}>
              <h3>Tạo Ưu Đãi Đặt Sớm</h3>
              <p>
                Có thời gian chuẩn bị kỹ lưỡng hơn khi nhận nhiều đặt phòng sớm hơn.
                Khuyến mãi này dành cho khách đặt nhiều ngày trước ngày nhận phòng.
              </p>
            </div>
          </div>
        </Card>

        <Card className={styles.Section}>
          <h3 className={styles.Title}>Thời gian đặt</h3>

          <div className={styles.FormField}>
            <p className={styles.FormFieldTitle}>
              Khách cần đặt với khuyến mãi này trước bao lâu?
            </p>

            <div className="d-flex gap-2 align-items-center">
              <AppInputNumberValidation
                name="minEarlyDays"
                keyName="minEarlyDays"
                placeholder="Chọn ngày"
                validation={validation}
                thousandSeparator=""
                formatOptions={{ decimalScale: 0 }}
                value={validation.values.minEarlyDays}
              />

              <span>ngày hoặc hơn</span>
            </div>
          </div>
        </Card>

        <FormCreatePromotion
          validation={validation}
          roomOfPlaceList={roomOfPlaceList}
          placePriceTypes={placePriceTypes}
        />

        <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
          <div className={styles.ButtonWrap}>
            <Link to={{
              pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
              search: `?place=${placeId}`,
            }}
            >
              <Button
                color={themeColor.SECONDARY}
              >
                Hủy
              </Button>
            </Link>

            <Button
              onClick={validation.handleSubmit}
              disabled={promotionDetail.discountStatus
                && promotionDetail.discountStatus !== PROMOTION_STATUS.PENDING}
            >
              {promotionId ? 'Cập nhật' : 'Tạo'}
            </Button>
          </div>
        </div>

        <ModalPromotionSummarize
          isOpen={isOpenModal}
          loading={isLoadingCreatePromotion}
          validation={validation}
          roomOfPlaceList={roomOfPlaceList}
          placePriceTypes={placePriceTypes}
          onCloseClick={onCloseModal}
          onSubmit={onSubmitCreatePromotion}
        />
      </div>
    </div>
  );
}

CreateEarlyPromotion.propTypes = {
  isLoadingCreatePromotion: PropTypes.bool.isRequired,

  roomOfPlaceList: PropTypes.instanceOf(Array),
  placePriceTypes: PropTypes.instanceOf(Array),
  promotionDetail: PropTypes.instanceOf(Object),

  actionGetListRoomsOfPlace: PropTypes.func.isRequired,
  actionGetPlacePriceTypes: PropTypes.func.isRequired,
  actionPostPromotion: PropTypes.func.isRequired,
  actionGetPromotionDetail: PropTypes.func.isRequired,
  actionEditPromotion: PropTypes.func.isRequired,
  actionClearDataPromotionDetail: PropTypes.func.isRequired,
};

CreateEarlyPromotion.defaultProps = {
  roomOfPlaceList: INITIAL_VALUE.ARRAY,
  placePriceTypes: INITIAL_VALUE.ARRAY,
  promotionDetail: INITIAL_VALUE.OBJECT,
};

export default CreateEarlyPromotion;
